import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import mapboxgl, { LngLatLike } from 'mapbox-gl';
import { MapboxAuthBaseService } from '@/app/shared/services/mapbox-auth-base.service';
import { AppConfigService } from '@/app/core/services/app-config.service';
import {
  HomePageMapViewModel,
  HomePageLandViewModel,
  DeviceLineViewModelListResponseResult,
  DeviceLineProxy,
  DeviceLineViewModel,
} from '@/app/proxy/proxies';
import { DoktarHQCoordinates } from '@/app/shared/consts/mapbox-consts';
import { LanguageInitializerService } from '@/app/core/services/language-initializer.service';
import {
  prepareMapboxRtlTextPluginForLanguage,
  setupMultilingualMapLanguageControls,
} from '@/app/shared/utils/mapbox-language.util';
import { MixpanelTrackerService } from '@/app/core/analytics/mixpanel-tracker.service';

export interface MapWidgetOptions {
  center?: LngLatLike;
  zoom?: number;
  style?: string;
}

export interface AssetCounts {
  landCount: number;
  plantationCount: number;
  deviceCount: number;
  totalArea: string;
}

interface MarkerData {
  marker: mapboxgl.Marker;
  type: 'land' | 'plantation' | 'device';
  id: string;
}

@Injectable({
  providedIn: 'root',
})
export class HomeMapWidgetService extends MapboxAuthBaseService implements OnDestroy {
  map: mapboxgl.Map | undefined;
  
  private mapLoadedSubject = new BehaviorSubject<boolean>(false);
  private assetCountsSubject = new BehaviorSubject<AssetCounts>({
    landCount: 0,
    plantationCount: 0,
    deviceCount: 0,
    totalArea: '0 ha',
  });
  
  public deviceLinesCache: DeviceLineViewModel[] = [];
  private deviceLinesLoaded = false;
  private mapDragHandlerRegistered = false;
  /** True while map is moving/zooming due to code (fitBounds/flyTo), not user */
  private programmaticMoveInProgress = false;

  private homeMapAnalyticsContext: { filterBy: string[]; fullScreen: boolean } = {
    filterBy: [],
    fullScreen: false,
  };
  
  // Store markers for proper cleanup and click handling
  private markers: MarkerData[] = [];
  private currentLands: HomePageLandViewModel[] = [];

  public mapLoaded$ = this.mapLoadedSubject.asObservable();
  public assetCounts$ = this.assetCountsSubject.asObservable();

  // Performance optimized constants
  private readonly MAP_LOAD_TIMEOUT = 10000;
  private readonly CONTAINER_DIMENSION_TIMEOUT = 5000;
  private readonly FIT_BOUNDS_DURATION = 600;
  private readonly MAP_PADDING = { top: 60, bottom: 60, left: 60, right: 60 };

  constructor(
    appConfigService: AppConfigService,
    private _deviceLineProxy: DeviceLineProxy,
    private languageInitializerService: LanguageInitializerService,
    private mixpanelTracker: MixpanelTrackerService
  ) {
    super(appConfigService);
    this.loadDeviceLines();
  }

  /**
   * Backwards-compatible no-op.
   * (Home Map Engaged is now tracked on every interaction.)
   */
  resetMapEngagedTracking(): void {
    // Intentionally left blank
  }

  setHomeMapAnalyticsContext(context: Partial<{ filterBy: string[]; fullScreen: boolean }>): void {
    if (context.filterBy) {
      this.homeMapAnalyticsContext.filterBy = context.filterBy;
    }
    if (typeof context.fullScreen === 'boolean') {
      this.homeMapAnalyticsContext.fullScreen = context.fullScreen;
    }
  }

  trackHomeMapEngaged(): void {
    this.mixpanelTracker.trackHomeMapEngaged({
      'filter by': this.homeMapAnalyticsContext.filterBy,
      'full screen': this.homeMapAnalyticsContext.fullScreen,
    });
  }

  /**
   * Load device lines once and cache them
   */
  private loadDeviceLines(): void {
    this._deviceLineProxy.deviceLine_GetAllForStandardUser().subscribe((res: DeviceLineViewModelListResponseResult) => {
      this.deviceLinesCache = res.result ?? [];
      this.deviceLinesLoaded = true;
    });
  }

  initializeMap(containerId: string, options?: MapWidgetOptions): Observable<mapboxgl.Map> {
    return new Observable((observer) => {
      const container = document.getElementById(containerId);
      if (!container) {
        observer.error(new Error(`Map container '${containerId}' not found`));
        return;
      }

      this.destroyMap();

      this.waitForContainerDimensions(containerId)
        .then(() => this.createMapInstance(containerId, options, observer))
        .catch((error) => observer.error(error));
    });
  }

  private async createMapInstance(
    containerId: string,
    options: MapWidgetOptions | undefined,
    observer: { next: (map: mapboxgl.Map) => void; error: (err: Error) => void; complete: () => void }
  ): Promise<void> {
    try {
      const accessToken = await this.getAccessToken();
      const languageCode = this.languageInitializerService.getCurrentLanguageCode();
      prepareMapboxRtlTextPluginForLanguage(languageCode);
      const style = options?.style || (await this.getMapboxStyle());

      this.map = new mapboxgl.Map({
        container: containerId,
        style,
        center: options?.center || (DoktarHQCoordinates as LngLatLike),
        zoom: options?.zoom || 6,
        accessToken,
        attributionControl: false,
        logoPosition: 'bottom-right',
        // Performance optimizations
        fadeDuration: 0,
        trackResize: true,
        refreshExpiredTiles: false,
      });

      setupMultilingualMapLanguageControls(this.map, languageCode);

      // Ignore Mapbox's initial zoomend/move after load; only track real user interaction
      this.programmaticMoveInProgress = true;

      this.setupMapEventHandlers(observer);
      this.setupMapLoadTimeout(observer);
    } catch (error) {
      observer.error(error as Error);
    }
  }

  private setupMapEventHandlers(observer: { next: (map: mapboxgl.Map) => void; error: (err: Error) => void; complete: () => void }): void {
    if (!this.map) return;

    if (!this.mapDragHandlerRegistered) {
      const trackMapEngaged = () => {
        // Do not track when map moved/zoomed by code (e.g. fitBounds, flyTo on load)
        if (this.programmaticMoveInProgress) {
          this.programmaticMoveInProgress = false;
          return;
        }

        this.trackHomeMapEngaged();
      };

      // Only user-initiated pan fires dragend (not programmatic pan)
      this.map.on('dragend', trackMapEngaged);
      // zoomend fires on both user zoom and programmatic zoom; programmatic ones are skipped via flag
      this.map.on('zoomend', trackMapEngaged);

      this.mapDragHandlerRegistered = true;
    }

    this.map.on('load', () => {
      console.log('Map loaded successfully');

      // After load, any zoomend from our fitBounds/flyTo will clear programmaticMoveInProgress.
      // If we never call fitBounds (e.g. no data), allow user tracking after a short delay.
      setTimeout(() => {
        this.programmaticMoveInProgress = false;
      }, 2500);

      this.mapLoadedSubject.next(true);
      observer.next(this.map!);
      observer.complete();
    });

    this.map.on('error', (error) => observer.error(error.error));
  }

  private setupMapLoadTimeout(observer: { error: (err: Error) => void }): void {
    setTimeout(() => {
      if (!this.mapLoadedSubject.getValue()) {
        observer.error(new Error('Map failed to load within timeout period'));
      }
    }, this.MAP_LOAD_TIMEOUT);
  }

  private waitForContainerDimensions(containerId: string): Promise<HTMLElement> {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();

      const checkDimensions = () => {
        const container = document.getElementById(containerId);
        if (!container) {
          reject(new Error(`Map container '${containerId}' not found`));
          return;
        }

        if (container.offsetWidth > 0 && container.offsetHeight > 0) {
          resolve(container);
          return;
        }

        if (Date.now() - startTime > this.CONTAINER_DIMENSION_TIMEOUT) {
          reject(new Error('Map container has no dimensions after waiting'));
          return;
        }

        requestAnimationFrame(checkDimensions);
      };

      checkDimensions();
    });
  }

  loadMapData(mapData: HomePageMapViewModel, skipFitBounds = false): void {
    if (!this.map) return;

    this.clearMapLayers();
    this.currentLands = mapData.lands || [];
    this.assetCountsSubject.next(this.calculateAssetCounts(mapData));

    if (this.currentLands.length) {
      this.createLandBoundaries(this.currentLands);
      this.createPlantationBoundaries(this.currentLands);
      this.addLandMarkers(this.currentLands);
      this.addPlantationMarkers(this.currentLands);
      this.addDeviceMarkers(this.currentLands);

      if (!skipFitBounds) {
        // Use setTimeout instead of waitForMapIdle for faster response
        setTimeout(() => {
          this.map?.resize();
          this.fitMapToAllAssets(this.currentLands);
        }, 50);
      }
    }
  }

  private calculateAssetCounts(mapData: HomePageMapViewModel): AssetCounts {
    let plantationCount = 0;
    let deviceCount = 0;

    mapData.lands?.forEach(land => {
      plantationCount += land.plantations?.length || 0;
      land.plantations?.forEach(p => {
        deviceCount += p.devices?.length || 0;
      });
    });

    return {
      landCount: mapData.lands?.length || 0,
      plantationCount,
      deviceCount,
      totalArea: mapData.totalAreaDisplay || '0 ha',
    };
  }

  private createLandBoundaries(lands: HomePageLandViewModel[]): void {
    if (!this.map || !lands.length) return;

    const features = lands.map(land => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Polygon' as const,
        coordinates: land.boundaries ? [land.boundaries.map(c => this.normalizeCoordinate(c))] : [],
      },
      properties: { id: land.id, name: land.name },
    }));

    this.map.addSource('land-boundaries', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features },
    });

    this.map.addLayer({
      id: 'land-boundaries-fill',
      type: 'fill',
      source: 'land-boundaries',
      paint: { 'fill-color': '#FFFFFF', 'fill-opacity': 0.25 },
    });

    this.map.addLayer({
      id: 'land-boundaries-line',
      type: 'line',
      source: 'land-boundaries',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': '#FFFFFF', 'line-width': 3 },
    });
  }

  private createPlantationBoundaries(lands: HomePageLandViewModel[]): void {
    if (!this.map) return;

    const features = lands.flatMap(land => 
      land.plantations?.map(p => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Polygon' as const,
          coordinates: p.boundaries ? [p.boundaries.map(c => this.normalizeCoordinate(c))] : [],
        },
        properties: { id: p.id, name: p.name },
      })) || []
    );

    if (!features.length) return;

    this.map.addSource('plantation-polygons', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features },
    });

    this.map.addLayer({
      id: 'plantation-fill',
      type: 'fill',
      source: 'plantation-polygons',
      paint: { 'fill-color': '#FFFFFF', 'fill-opacity': 0.3 },
    });

    this.map.addLayer({
      id: 'plantation-boundaries-line',
      type: 'line',
      source: 'plantation-polygons',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': '#FFFFFF', 'line-width': 2 },
    });
  }

  private normalizeCoordinate(coord: number[]): [number, number] {
    if (!coord || coord.length < 2) return [0, 0];
    const [first, second] = coord;
    // Turkey region: swap if lat/lng are reversed
    return (first >= 36 && first <= 42 && second >= 26 && second <= 45) 
      ? [second, first] 
      : [first, second];
  }

  private calculateCentroid(boundaries: number[][] | undefined): [number, number] {
    if (!boundaries?.length) return [0, 0];

    let minLng = Infinity, maxLng = -Infinity;
    let minLat = Infinity, maxLat = -Infinity;

    for (const coord of boundaries) {
      if (coord?.length >= 2) {
        const [lng, lat] = this.normalizeCoordinate(coord);
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      }
    }

    return [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
  }

  private addLandMarkers(lands: HomePageLandViewModel[]): void {
    lands.forEach(land => {
      const centroid = this.calculateCentroid(land.boundaries);
      this.createMarker('land', land.id, centroid, 40);
    });
  }

  private addPlantationMarkers(lands: HomePageLandViewModel[]): void {
    lands.forEach(land => {
      land.plantations?.forEach(plantation => {
        const centroid = this.calculateCentroid(plantation.boundaries);
        this.createMarker('plantation', plantation.id, centroid, 36);
      });
    });
  }

  private addDeviceMarkers(lands: HomePageLandViewModel[]): void {
    lands.forEach(land => {
      land.plantations?.forEach(plantation => {
        plantation.devices?.forEach(device => {
          if (device.location?.longitude != null && device.location?.latitude != null &&
              !isNaN(device.location.longitude) && !isNaN(device.location.latitude)) {
            
            const coords: [number, number] = [device.location.longitude, device.location.latitude];
            const iconHtml = this.createDeviceIcon(device.deviceLineCode);
            this.createMarker('device', device.accessKey!, coords, 32, iconHtml);
          }
        });
      });
    });
  }

  private createMarker(
    type: 'land' | 'plantation' | 'device',
    id: string,
    coordinates: [number, number],
    size = 40,
    customIcon?: string
  ): void {
    if (!this.map) return;

    const container = document.createElement('div');
    container.className = `custom-map-marker marker-${type}`;
    container.style.cssText = `
      width: ${size}px; height: ${size}px;
      background: #FFFFFF; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 2px 6px rgba(0,0,0,0.25);
    `;
    container.innerHTML = customIcon || '<i class="ph-fill ph-map-pin"></i>';

    const marker = new mapboxgl.Marker({ element: container, anchor: 'center' })
      .setLngLat(coordinates)
      .addTo(this.map);

    this.markers.push({ marker, type, id });
  }

  private createDeviceIcon(deviceLineCode: string | undefined): string {
    if (!deviceLineCode || !this.deviceLinesLoaded) {
      return '<i class="ph-fill ph-cpu"></i>';
    }

    const deviceLine = this.deviceLinesCache.find(d => d.code === deviceLineCode);
    if (!deviceLine?.iconURL) {
      return '<i class="ph-fill ph-cpu"></i>';
    }

    return `<img src="${deviceLine.iconURL}" alt="" style="width:20px;height:20px;object-fit:contain" onerror="this.outerHTML='<i class=ph-fill ph-cpu></i>'" />`;
  }

  /**
   * Zoom to fit ALL lands in the enterprise
   */
  public zoomToAllAssets(): void {
    if (this.currentLands.length) {
      this.fitMapToAllAssets(this.currentLands);
    }
  }

  private fitMapToAllAssets(lands: HomePageLandViewModel[]): void {
    if (!this.map || !lands.length) return;

    const bounds = new mapboxgl.LngLatBounds();
    let hasValid = false;

    // Include all land and plantation boundaries
    for (const land of lands) {
      for (const coord of land.boundaries || []) {
        if (coord?.length >= 2 && !isNaN(coord[0]) && !isNaN(coord[1])) {
          bounds.extend(this.normalizeCoordinate(coord));
          hasValid = true;
        }
      }
      for (const plantation of land.plantations || []) {
        for (const coord of plantation.boundaries || []) {
          if (coord?.length >= 2 && !isNaN(coord[0]) && !isNaN(coord[1])) {
            bounds.extend(this.normalizeCoordinate(coord));
            hasValid = true;
          }
        }
      }
    }

    if (!hasValid) return;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const isSmall = Math.abs(ne.lng - sw.lng) < 0.001 && Math.abs(ne.lat - sw.lat) < 0.001;

    if (isSmall) {
      this.map.flyTo({
        center: [bounds.getCenter().lng, bounds.getCenter().lat],
        zoom: 15,
        duration: this.FIT_BOUNDS_DURATION,
      });
    } else {
      this.map.fitBounds(bounds, {
        padding: this.MAP_PADDING,
        duration: this.FIT_BOUNDS_DURATION,
        maxZoom: 16,
      });
    }
  }

  clearMapLayers(): void {
    if (!this.map) return;

    // Remove all markers properly to prevent memory leaks
    this.markers.forEach(({ marker }) => marker.remove());
    this.markers = [];

    // Remove layers and sources
    const layers = ['plantation-boundaries-line', 'plantation-fill', 'land-boundaries-line', 'land-boundaries-fill'];
    const sources = ['plantation-polygons', 'land-boundaries'];

    layers.forEach(id => {
      if (this.map!.getLayer(id)) this.map!.removeLayer(id);
    });

    sources.forEach(id => {
      if (this.map!.getSource(id)) this.map!.removeSource(id);
    });
  }

  getMap(): mapboxgl.Map | undefined {
    return this.map;
  }

  private destroyMap(): void {
    if (this.map) {
      this.clearMapLayers();
      this.map.remove();
      this.map = undefined;
      this.mapLoadedSubject.next(false);
      // Allow event handlers to be re-registered for the next map instance
      this.mapDragHandlerRegistered = false;
    }
  }

  ngOnDestroy(): void {
    this.destroyMap();
  }
}
